import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { io, Socket } from 'socket.io-client';

export interface WordTimestamp {
  word: string;
  start: number; // in seconds
  end: number;   // in seconds
}


@Injectable({
  providedIn: 'root'
})
export class BotSocketService {

  private socket!: Socket;
  private aiSocketUrl = `https://memailllm.canstringai.com/`;
  private SAMPLE_RATE = 16000;

  public wordTimestamps$ = new BehaviorSubject<WordTimestamp[]>([]);
  public subtitles$ = new Subject<string>();
  public streamComplete$ = new Subject<void>();

  private audioContext = new AudioContext({
    sampleRate: 16000,
    latencyHint: 'interactive'
  });

  private scheduledTime = 0;
  private isPlaying = false;
  private audioQueue: ArrayBuffer[] = [];
  private MIN_BUFFER_MS = 100; // 100ms buffer
  private bufferedDuration = 0;

  constructor(private ngZone: NgZone) {
    this.init();
  }

  private init() {
    this.socket = io(this.aiSocketUrl);

    this.audioContext = new (window.AudioContext ||
      (window as any).webkitAudioContext)({
        sampleRate: this.SAMPLE_RATE
      });

    this.socket.on('word_timestamp', (data: WordTimestamp) => {
      this.ngZone.run(() => {
        this.wordTimestamps$.next([
          ...this.wordTimestamps$.value,
          data
        ]);
        this.subtitles$.next(data.word);
      });
    });

    this.socket.on('audio_chunk', (data: { chunk: string }) => {
      this.audioQueue.push(this.base64ToArrayBuffer(data.chunk));
      if (!this.isPlaying) this.playAudioQueue();
    });

    this.socket.on('stream_complete', () => {
      this.isPlaying = false;
      this.ngZone.run(() => this.streamComplete$.next());
    });
  }

  /** Needed for browser autoplay restriction */
  async resumeAudio() {
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }
  }

  startStreaming(text: string, voice = 'af_bella') {
    this.wordTimestamps$.next([]);
    this.audioQueue = [];
    this.isPlaying = false;

    this.socket.emit('start_streaming', {
      text,
      voice,
      sample_rate: this.SAMPLE_RATE
    });
  }

  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  }

  private async playAudioQueue() {
    if (this.isPlaying) return;

    this.isPlaying = true;

    // Initialize scheduledTime correctly
    this.scheduledTime = Math.max(
      this.audioContext.currentTime,
      this.scheduledTime
    );

    while (this.audioQueue.length) {
      const buffer = this.audioQueue.shift()!;
      await this.playPCM(buffer);
    }

    this.isPlaying = false;
  }

  private playPCM(buffer: ArrayBuffer): Promise<void> {
    return new Promise(resolve => {
      const pcm16 = new Int16Array(buffer);
      const float32 = new Float32Array(pcm16.length);
      for (let i = 0; i < pcm16.length; i++) {
        let sample = pcm16[i] / 32768.0;
        sample = Math.max(-1.0, Math.min(1.0, sample));
        float32[i] = sample;
      }
      const audioBuffer = this.audioContext.createBuffer(
        1,
        float32.length,
        this.audioContext.sampleRate
      );

      audioBuffer.getChannelData(0).set(float32);

      const source: any = this.audioContext.createBufferSource();
      source.buffer = audioBuffer;

      const gainNode = this.audioContext.createGain();
      gainNode.gain.value = 0.9;

      source.connect(gainNode);
      gainNode.connect(this.audioContext.destination);


      const startTime = this.scheduledTime;
      source.start(startTime);

      this.scheduledTime += audioBuffer.duration;

      source.onended = resolve;
    });
  }

  stopStreaming(): void {

    console.log('Stopping AI audio streaming');

    this.audioQueue = [];
    try {
      this.audioContext?.suspend();
    } catch (e) {
      console.warn('AudioContext suspend failed', e);
    }
    this.isPlaying = false;
    if (this.socket?.connected) {
      this.socket.emit('stop_streaming');
    }
    this.wordTimestamps$.next([]);
  }

  onBotAudioChunk(base64Audio: string) {
    const buffer = this.base64ToArrayBuffer(base64Audio);

    // PCM16 duration calculation
    const samples = buffer.byteLength / 2;
    const duration = samples / this.audioContext.sampleRate;

    this.bufferedDuration += duration;
    this.audioQueue.push(buffer);

    this.playAudioQueue();
  }

}
